import React from 'react';
import { Handle, Position } from 'reactflow';
import { WorkflowStep } from '../../types/workflow';
import { Settings, Shield, Zap, Package, Target, ChevronDown } from 'lucide-react';

interface N8nSystemStackProps {
  data: {
    systemSteps: WorkflowStep[];
    onEditStep: (step: WorkflowStep) => void;
  };
}

const N8nSystemStack: React.FC<N8nSystemStackProps> = ({ data }) => {
  const { systemSteps, onEditStep } = data;

  const getSystemNodeIcon = (step: WorkflowStep) => {
    switch (step.name) {
      case 'prepare-authdata': return <Shield size={14} className="text-blue-600" />;
      case 'target-calldata': return <Target size={14} className="text-green-600" />;
      case 'sca-calldata': return <Zap size={14} className="text-yellow-600" />;
      case 'bundle': return <Package size={14} className="text-purple-600" />;
      default: return <Settings size={14} className="text-gray-600" />;
    }
  };

  console.log(systemSteps);


  const getSystemNodeColor = (step: WorkflowStep) => {
    switch (step.name) {
      case 'prepare-authdata': return 'bg-blue-50 border-blue-200';
      case 'target-calldata': return 'bg-green-50 border-green-200';
      case 'sca-calldata': return 'bg-yellow-50 border-yellow-200';
      case 'bundle': return 'bg-purple-50 border-purple-200';
      default: return 'bg-gray-50 border-gray-200';
    }
  };

  const getSystemNodeLabel = (step: WorkflowStep) => {
    switch (step.name) {
      case 'prepare-authdata': return 'Auth Data';
      case 'target-calldata': return 'Target Calldata';
      case 'sca-calldata': return 'SCA Calldata';
      case 'bundle': return 'Bundle';
      default: return 'System';
    }
  };

  const SystemNode = ({ step }: { step: WorkflowStep }) => (
    <div className="bg-n8n-dark-700 border border-n8n-dark-600 rounded-n8n p-3 shadow-n8n hover:shadow-n8n-lg transition-all duration-200 group opacity-95 hover:border-n8n-dark-500">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2 flex-1 min-w-0">
          <div className="flex-shrink-0">
            {getSystemNodeIcon(step)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-medium text-n8n-dark-200 text-sm truncate">{step.name}</div>
            <div className="text-xs text-n8n-dark-400">{getSystemNodeLabel(step)}</div>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <button 
            onClick={() => onEditStep(step)}
            className="opacity-0 group-hover:opacity-100 p-1 hover:bg-n8n-dark-600 rounded transition-all duration-200"
          >
            <Settings size={12} className="text-n8n-dark-300" />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="group relative">
      {/* System Stack Container */}
      <div className="bg-n8n-dark-900/30 border-2 border-dashed border-n8n-dark-500/50 rounded-n8n p-4 min-w-[260px] shadow-n8n-lg hover:shadow-n8n-lg transition-all duration-200">
        {/* Stack Header */}
        <div className="flex items-center justify-between mb-3 pb-2 border-b border-n8n-dark-500/30">
          <div className="flex items-center space-x-2">
            <div className="w-6 h-6 bg-n8n-dark-600 rounded-full flex items-center justify-center shadow-n8n">
              <span className="text-white text-xs font-bold">S</span>
            </div>
            <span className="font-semibold text-n8n-dark-200 text-sm">System</span>
          </div>
          <span className="text-xs text-n8n-dark-300 bg-n8n-dark-600/50 px-2 py-1 rounded-full">
            {systemSteps.length} steps
          </span>
        </div>

        {/* Stack Items */}
        <div className="space-y-2">
          {systemSteps.map((step, index) => (
            <div key={step.name} className="relative">
              <SystemNode step={step} />
              
              {/* Vertical Arrow between items */}
              {index < systemSteps.length - 1 && (
                <div className="flex justify-center py-1">
                  <ChevronDown size={16} className="text-n8n-dark-500" />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Connection Handles */}
      <Handle
        type="target"
        position={Position.Left}
        className="w-3 h-3 bg-n8n-dark-500 border-2 border-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-n8n"
        style={{ left: -8 }}
      />
    </div>
  );
};

export default N8nSystemStack;